import axios from 'axios';
import { API_BASE } from './api';
import { assertBlobKind, parseBlobError } from './blobDownload';
import { isMobileContainer, isNativePlatform } from './device';

const publicApi = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
});

/** Public PDF URL for invoice-portal token (inline display or download). */
export function getFeeReceiptPdfUrl(token) {
  if (!token) return '';
  return `${API_BASE}/api/public/invoice-portal/pdf?token=${encodeURIComponent(token)}`;
}

/** Fetch fee receipt / salary slip PDF as Blob for in-app preview. */
export async function fetchFeeReceiptPdfBlob(token) {
  if (!token) throw new Error('Invalid receipt link');

  const res = await publicApi.get('public/invoice-portal/pdf', {
    params: { token },
    responseType: 'blob',
  });

  const blob = res.data;
  const msg = await parseBlobError(blob);
  if (msg) throw new Error(msg);
  await assertBlobKind(blob, 'pdf');
  return blob;
}

/** Shareable document portal URL (fee receipt / salary slip). */
export function getDocumentPortalBaseUrl() {
  if (isNativePlatform() || isMobileContainer()) {
    if (process.env.REACT_APP_PUBLIC_WEB_URL) {
      return process.env.REACT_APP_PUBLIC_WEB_URL.replace(/\/$/, '');
    }
    if (process.env.REACT_APP_INVOICE_PORTAL_BASE) {
      return process.env.REACT_APP_INVOICE_PORTAL_BASE.replace(/\/$/, '');
    }
  }
  if (typeof window !== 'undefined') {
    const { hostname, origin } = window.location;
    const publicPath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${origin}${publicPath}`;
    }
  }
  if (process.env.REACT_APP_INVOICE_PORTAL_BASE) {
    return process.env.REACT_APP_INVOICE_PORTAL_BASE.replace(/\/$/, '');
  }
  if (process.env.REACT_APP_PUBLIC_WEB_URL) {
    return process.env.REACT_APP_PUBLIC_WEB_URL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    const publicPath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    return `${window.location.origin}${publicPath}`;
  }
  return '';
}

/** In-app hash route for HashRouter: #/invoice-portal?token=... */
export function getDocumentPortalHash(token) {
  if (!token) return '';
  return `#/invoice-portal?token=${encodeURIComponent(token)}`;
}

/** External/share URL (legacy format): https://host#invoice-portal?token=... */
export function getDocumentPortalUrl(token) {
  if (!token) return '';
  const base = getDocumentPortalBaseUrl();
  return `${base}#/invoice-portal?token=${encodeURIComponent(token)}`;
}

/** Full URL for in-app HashRouter slip/receipt (new tab only). */
export function getDocumentPortalWindowUrl(token) {
  if (!token || typeof window === 'undefined') return '';
  const hash = getDocumentPortalHash(token);
  return `${window.location.origin}${window.location.pathname}${hash}`;
}

function closeReservedWindow(reservedWindow) {
  if (!reservedWindow || reservedWindow.closed) return;
  try {
    reservedWindow.close();
  } catch {
    // ignore
  }
}

function navigateReservedWindow(reservedWindow, url) {
  if (!reservedWindow || reservedWindow.closed || !url) return false;
  try {
    reservedWindow.location.replace(url);
    reservedWindow.focus?.();
    return true;
  } catch {
    return false;
  }
}

/** Extract portal token from /invoice-portal?token=... style links. */
export function extractPortalTokenFromLink(link) {
  let raw = String(link || '').trim();
  if (raw.startsWith('/#/')) raw = raw.slice(2);
  const pathPart = raw.split('?')[0];
  if (pathPart !== '/invoice-portal' && !pathPart.startsWith('/invoice-portal')) {
    return null;
  }
  const qIndex = raw.indexOf('?');
  if (qIndex === -1) return null;
  return new URLSearchParams(raw.slice(qIndex + 1)).get('token');
}

/** Navigate to invoice portal in-app (native hash) or new tab (web). */
export function navigateToDocumentPortal(token) {
  if (!token) return false;
  return openDocumentPortal(token, null);
}

/**
 * Open document portal in a new tab only — never changes the current tab hash.
 * @param {string} token
 * @param {Window|null} reservedWindow Tab opened synchronously on user click (empty tab, no noopener)
 */
export function openDocumentPortal(token, reservedWindow = null) {
  if (!token) return false;

  if (isNativePlatform()) {
    const hash = getDocumentPortalHash(token);
    if (hash) {
      window.location.hash = hash;
      return true;
    }
    return false;
  }

  const url = getDocumentPortalWindowUrl(token) || getDocumentPortalUrl(token);
  if (!url) return false;

  if (navigateReservedWindow(reservedWindow, url)) {
    return true;
  }

  closeReservedWindow(reservedWindow);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  return opened != null;
}

export async function resolveFeeReceiptToken(payment, apiClient) {
  if (payment?.id && apiClient) {
    try {
      const res = await apiClient.get(`fees/receipt/${payment.id}`);
      const token = String(res.data?.data?.portal_token || '').trim();
      if (token) return token;
    } catch (_) {
      // fall through to cached token
    }
  }

  const cached = String(payment?.portal_token || '').trim();
  return cached || null;
}

export async function openFeeReceiptPortal(payment, apiClient) {
  const token = await resolveFeeReceiptToken(payment, apiClient);
  if (!token) throw new Error('Receipt link unavailable');
  if (!navigateToDocumentPortal(token)) {
    throw new Error('Could not open receipt. Allow pop-ups for this site.');
  }
  return token;
}

export async function openSalarySlipPortal(payment, apiClient, reservedWindow = null) {
  let token = String(payment?.portal_token || '').trim();
  if (!token && payment?.id && apiClient) {
    try {
      const res = await apiClient.get(`teachers/payroll/${payment.id}/portal-token`);
      token = String(res.data?.data?.portal_token || '').trim();
    } catch (_) {
      // ignore
    }
  }
  if (!token) throw new Error('Salary slip link unavailable');
  if (!openDocumentPortal(token, reservedWindow)) {
    if (reservedWindow && !reservedWindow.closed) {
      reservedWindow.close();
    }
    throw new Error('Could not open salary slip. Allow pop-ups for this site.');
  }
  return token;
}

/** Parse legacy hash: #invoice-portal?token=... or #/invoice-portal?token=... */
export function parseLegacyInvoicePortalHash(hash = '') {
  const raw = String(hash || '');
  const legacy = raw.match(/^#invoice-portal\?([^#]+)/);
  if (legacy) return new URLSearchParams(legacy[1]).get('token');
  const routed = raw.match(/^#\/invoice-portal\?([^#]+)/);
  if (routed) return new URLSearchParams(routed[1]).get('token');
  return null;
}
